import { useEffect, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, CircularProgress, FormControl, InputLabel, MenuItem, Paper, Select, Stack, TextField, Typography,
} from '@mui/material';

import SnackbarUtils from '../../utils/SnackbarUtils';
import {
  assistantErrorMessage, deleteAssistantSettings, getAssistantSettings, listAssistantModels, saveAssistantSettings,
} from '../../services/AssistantService';

export default function AssistantSettingsPanel({ onChanged }) {
  const [settings, setSettings] = useState(null);
  const [provider, setProvider] = useState('');
  const [model, setModel] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [models, setModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [modelsMessage, setModelsMessage] = useState('');

  const apply = (data) => {
    setSettings(data);
    setProvider(data.provider || data.providers?.[0]?.key || '');
    setModel(data.model || '');
    setApiKey('');
  };

  useEffect(() => {
    let ignore = false;
    getAssistantSettings()
      .then((data) => { if (!ignore) apply(data); })
      .catch((e) => { if (!ignore) setError(assistantErrorMessage(e)); });
    return () => { ignore = true; };
  }, []);

  if (error && !settings) return <Alert severity="error">{error}</Alert>;
  if (!settings) return <CircularProgress size={24} />;

  const providerInfo = settings.providers.find((item) => item.key === provider);
  // Con la clave guardada del mismo proveedor alcanza; si no, hay que pegarla para poder preguntar qué modelos tiene.
  const canListModels = !loadingModels && (apiKey.trim().length > 0 || (settings.configured && provider === settings.provider));

  const changeProvider = (value) => {
    setProvider(value);
    setModels([]);
    setModelsMessage('');
  };

  const loadModels = async () => {
    setLoadingModels(true);
    setError('');
    setModelsMessage('');
    try {
      const list = await listAssistantModels({ provider, apiKey: apiKey.trim() });
      setModels(list);
      setModelsMessage(list.length ? `${list.length} modelos disponibles para tu clave. Elegí uno de la lista.` : 'El proveedor no devolvió modelos para esta clave.');
    } catch (e) {
      setError(assistantErrorMessage(e));
    } finally {
      setLoadingModels(false);
    }
  };
  const canSave = !saving && (settings.configured ? true : apiKey.trim().length > 0);

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const saved = await saveAssistantSettings({ provider, model: model.trim(), apiKey: apiKey.trim() });
      apply(saved);
      SnackbarUtils.success('Configuración del asistente guardada');
      onChanged?.();
    } catch (e) {
      setError(assistantErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('¿Quitar la clave? El asistente dejará de responder hasta que cargues otra.')) return;
    setSaving(true);
    setError('');
    try {
      apply(await deleteAssistantSettings());
      SnackbarUtils.success('Se quitó la clave del asistente');
      onChanged?.();
    } catch (e) {
      setError(assistantErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>Proveedor de IA</Typography>
          <Typography variant="body2" color="text.secondary">
            El asistente usa la clave de tu empresa, así que el uso se factura en tu cuenta del proveedor.
          </Typography>
        </Box>

        <Alert severity="info">
          Para responder, el asistente consulta tus datos en modo solo lectura y envía los resultados al proveedor que elijas.
          Cada pregunta y respuesta queda guardada en el historial de tu empresa.
        </Alert>

        <FormControl size="small" fullWidth>
          <InputLabel>Proveedor</InputLabel>
          <Select label="Proveedor" value={provider} onChange={(event) => changeProvider(event.target.value)}>
            {settings.providers.map((item) => <MenuItem key={item.key} value={item.key}>{item.label}</MenuItem>)}
          </Select>
        </FormControl>

        {providerInfo?.notice && <Alert severity="warning">{providerInfo.notice}</Alert>}

        <Box>
          <Autocomplete
            freeSolo size="small" options={models} inputValue={model}
            onInputChange={(_, value) => setModel(value)}
            renderInput={(params) => (
              <TextField
                {...params} label="Modelo (opcional)" placeholder={providerInfo?.defaultModel || ''}
                helperText={modelsMessage || (providerInfo ? `Si lo dejás vacío se usa ${providerInfo.defaultModel}. Si da error de modelo, mirá cuáles tiene tu clave.` : '')}
              />
            )}
          />
          <Button size="small" onClick={loadModels} disabled={!canListModels} sx={{ mt: 0.5, textTransform: 'none' }}>
            {loadingModels ? 'Consultando…' : 'Ver modelos disponibles'}
          </Button>
          {!canListModels && !loadingModels && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>Pegá la clave de API para consultarlos.</Typography>
          )}
        </Box>

        <TextField
          size="small" fullWidth type="password" autoComplete="new-password" label="Clave de API" value={apiKey}
          onChange={(event) => setApiKey(event.target.value)}
          placeholder={settings.configured ? `Clave guardada (${settings.keyHint})` : 'Pegá tu clave de API'}
          helperText={settings.configured
            ? 'Dejala vacía para conservar la actual. Se prueba con el proveedor antes de guardarse.'
            : 'Se prueba con el proveedor antes de guardarse. Nunca se vuelve a mostrar completa.'}
        />

        {error && <Alert severity="error">{error}</Alert>}

        <Stack direction="row" spacing={1.5}>
          <Button variant="contained" onClick={save} disabled={!canSave}>
            {saving ? 'Guardando…' : 'Guardar'}
          </Button>
          {settings.configured && (
            <Button color="error" onClick={remove} disabled={saving}>Quitar clave</Button>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}
